import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { Activity, Clock, CheckCircle, XCircle, AlertTriangle, Server, Database, Zap } from 'lucide-react'
import { fetchXpertLogs, fetchXpertStats, fetchHotelStatus } from '../services/api'

const XpertMonitoring = ({ activeEndpoint }) => {
  // Room type mapping for better readability
  const roomTypes = {
    'STD': 'Standard Room',
    'DLX': 'Deluxe Room',
    'STE': 'Suite',
    'FAM': 'Family Room',
    'EXE': 'Executive Room',
    'PRE': 'Premier Room',
    'VIL': 'Villa',
    'PEN': 'Penthouse'
  }

  // Fetch hotel data to get real hotel names
  const { data: hotelData = [] } = useQuery({
    queryKey: ['hotel-status'],
    queryFn: fetchHotelStatus,
    refetchInterval: 180000, // Refetch every 3 minutes
    staleTime: 60000, // Consider data stale after 1 minute
  })

  // Fetch logs for the active endpoint
  const { data: logs = [], isLoading: logsLoading, error: logsError } = useQuery({
    queryKey: ['xpert-logs', activeEndpoint],
    queryFn: () => fetchXpertLogs(activeEndpoint, 50),
    refetchInterval: 180000, // Refetch every 3 minutes for live monitoring
    staleTime: 10000, // Consider data stale after 10 seconds
  })

  // Fetch stats for the active endpoint
  const { data: statsData, isLoading: statsLoading, error: statsError } = useQuery({
    queryKey: ['xpert-stats', activeEndpoint],
    queryFn: () => fetchXpertStats(activeEndpoint, '24h'),
    refetchInterval: 180000, // Refetch every 3 minutes
    staleTime: 30000, // Consider data stale after 30 seconds
  })

  // Process stats data or use defaults
  const stats = statsData || {
    total: 0,
    success: 0,
    failed: 0,
    avgResponseTime: 0
  }

  // Helper function to get readable hotel name using real hotel data
  const getHotelDisplayName = (log) => {
    // First check if log already has hotel_name
    if (log.hotel_name) {
      return log.hotel_name
    }
    
    // Process hotel data similar to HotelGrid.jsx
    let hotels = []
    
    if (hotelData && Array.isArray(hotelData)) {
      // If hotelData is directly an array
      hotels = hotelData
    } else if (hotelData && hotelData.hotels) {
      if (Array.isArray(hotelData.hotels)) {
        // If hotels is directly an array
        hotels = hotelData.hotels
      } else if (hotelData.hotels.data && Array.isArray(hotelData.hotels.data)) {
        // If hotels is paginated with data property
        hotels = hotelData.hotels.data
      } else if (typeof hotelData.hotels === 'object') {
        // If hotels is an object, try to extract array values
        const hotelValues = Object.values(hotelData.hotels)
        const hotelArray = hotelValues.find(val => Array.isArray(val))
        if (hotelArray) {
          hotels = hotelArray
        }
      }
    }
    
    // Find hotel in the processed hotel data by ID
    const hotel = hotels.find(h => h.id === log.hotel_id)
    if (hotel) {
      return hotel.title || hotel.name || `Hotel #${log.hotel_id}`
    }
    
    // Fallback to Hotel #ID format
    return `Hotel #${log.hotel_id}`
  }

  // Helper function to get readable room info
  const getRoomDisplayInfo = (log) => {
    if (log.room_type) {
      const roomName = roomTypes[log.room_type] || log.room_type
      return log.room_number ? `${roomName} ${log.room_number}` : roomName
    }
    if (log.room_id) {
      return `Room #${log.room_id}`
    }
    return null
  }

  const getEndpointInfo = (endpoint) => {
    const info = {
      reservations: {
        title: 'Reservations API',
        description: 'Monitor getReservations endpoint calls',
        icon: <Database className="w-5 h-5" />,
        color: 'blue'
      },
      inventory: {
        title: 'Room Inventory API',
        description: 'Monitor updateRoomInventory endpoint calls',
        icon: <Server className="w-5 h-5" />,
        color: 'green'
      },
      confirmations: {
        title: 'Confirmations API',
        description: 'Monitor confirmReservation endpoint calls',
        icon: <Zap className="w-5 h-5" />,
        color: 'purple'
      }
    }
    return info[endpoint] || info.reservations
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-4 h-4 text-green-500" />
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-500" />
      case 'warning':
        return <AlertTriangle className="w-4 h-4 text-yellow-500" />
      default:
        return <Activity className="w-4 h-4 text-gray-500" />
    }
  }

  const getStatusBadge = (status) => {
    const classes = {
      success: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      warning: 'bg-yellow-100 text-yellow-800'
    }
    return `px-2 py-1 rounded-full text-xs font-medium ${classes[status] || 'bg-gray-100 text-gray-800'}`
  }

  const formatTime = (date) => {
    // Handle invalid dates
    if (!date || isNaN(new Date(date).getTime())) {
      return 'Invalid Date'
    }
    
    return new Intl.DateTimeFormat('id-ID', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }).format(new Date(date))
  }

  const formatTimeAgo = (date) => {
    // Handle invalid dates
    if (!date || isNaN(new Date(date).getTime())) {
      return 'Unknown'
    }
    
    const now = new Date()
    const targetDate = new Date(date)
    const diffInMinutes = Math.floor((now - targetDate) / (1000 * 60))
    
    if (diffInMinutes < 1) return 'Just now'
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`
    
    const diffInHours = Math.floor(diffInMinutes / 60)
    if (diffInHours < 24) return `${diffInHours}h ago`
    
    const diffInDays = Math.floor(diffInHours / 24)
    return `${diffInDays}d ago`
  }

  const filteredLogs = logs || []
  const currentStats = stats || { total: 0, success: 0, failed: 0, avgResponseTime: 0 }
  const endpointInfo = getEndpointInfo(activeEndpoint)

  // Show loading state
  if (logsLoading || statsLoading) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="animate-pulse">
            <div className="h-6 bg-gray-200 rounded w-1/4 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white rounded-lg shadow p-4">
              <div className="animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-8 bg-gray-200 rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  // Show error state
  if (logsError || statsError) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-center">
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Data</h3>
            <p className="text-gray-600">
              {logsError?.message || statsError?.message || 'Failed to load monitoring data'}
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-lg bg-${endpointInfo.color}-100`}>
              {endpointInfo.icon}
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{endpointInfo.title}</h2>
              <p className="text-gray-600">{endpointInfo.description}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
            <span className="text-sm text-gray-600">Live Monitoring</span>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Recent Activity</h3>
        </div>
        <div className="divide-y divide-gray-200">
          {filteredLogs.length === 0 ? (
            <div className="px-6 py-8 text-center">
              <Activity className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No recent activity for this endpoint</p>
            </div>
          ) : (
            filteredLogs.map((log) => (
              <div key={log.id} className="px-6 py-4 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {getStatusIcon(log.status)}
                    <div>
                      <div className="flex items-center space-x-2">
                        <span className="font-medium text-gray-900">{getHotelDisplayName(log)}</span>
                        {getRoomDisplayInfo(log) && (
                          <span className="text-sm text-blue-600 bg-blue-50 px-2 py-1 rounded">
                            {getRoomDisplayInfo(log)}
                          </span>
                        )}
                        <span className="text-xs text-gray-400">ID: {log.hotel_id}</span>
                        <span className={getStatusBadge(log.status)}>{log.status}</span>
                      </div>
                      <div className="flex items-center space-x-4 mt-1 text-sm text-gray-500">
                        <span className="font-medium">{log.method || 'POST'}</span>
                        <span className="text-green-600">{log.response_time}ms</span>
                        {log.request_size && log.response_size && (
                          <span className="text-blue-600">{log.request_size} → {log.response_size}</span>
                        )}
                        {log.reservations_count && (
                          <span className="text-purple-600">📋 {log.reservations_count} reservations</span>
                        )}
                        {log.rooms_updated && (
                          <span className="text-orange-600">🛏️ {log.rooms_updated} rooms updated</span>
                        )}
                        {log.guest_count && (
                          <span className="text-indigo-600">👥 {log.guest_count} guests</span>
                        )}
                      </div>
                      {log.error && (
                        <div className="mt-1 text-sm text-red-600">
                          Error: {log.error}
                        </div>
                      )}
                      {log.warning && (
                        <div className="mt-1 text-sm text-yellow-600">
                          Warning: {log.warning}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-gray-900">
                      {formatTime(log.created_at || log.timestamp)}
                    </div>
                    <div className="text-xs text-gray-500">
                      {formatTimeAgo(log.created_at || log.timestamp)}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}

export default XpertMonitoring